import {
  CUSTOM_MARKET_LABELS,
  type CustomAllowance,
  type CustomForm,
  type CustomItem,
  type CustomMilestone,
  type CustomOption,
  type CustomSection,
  type CustomUnitPrice,
  type CustomChatEntry,
  type CustomChatEvent,
  type ProposalDetail,
  customTotals,
  extend,
  formSov,
  formatPercentMilli,
  formatQuantity,
  newPartId,
  optionLabel,
  parsePercentMilli,
  parseQuantity,
} from "@nmt/shared";
import { useMutation } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { errorMessage, get, patch, post } from "../api";
import { FredText, streamPost } from "../components/fred/FredPanel";
import { Alert, Button, Checkbox, Field, Panel, PanelHeader, SelectField, TextAreaField, cx, formatCents, parseDollars } from "../components/ui";
import { LinkDialog } from "./Proposal";
import type { SendHow } from "../components/email-dialog";
import { NumberBox, RetailSendDialog } from "./RetailProposal";

const dollars = (c: number) => (c / 100).toFixed(2);
const percentBox = (t: string) => {
  const n = parsePercentMilli(t);
  return n === null ? Number.NaN : n;
};
const qtyBox = (t: string) => {
  const n = parseQuantity(t);
  return n === null ? Number.NaN : n;
};
const linesOf = (t: string) => t.split("\n");

/** Replaces the item with this id in a list. */
function swap<T extends { id: string }>(list: T[], id: string, next: Partial<T>): T[] {
  return list.map((x) => (x.id === id ? { ...x, ...next } : x));
}
/** Moves the item with this id up (-1) or down (+1). */
function move<T extends { id: string }>(list: T[], id: string, by: -1 | 1): T[] {
  const i = list.findIndex((x) => x.id === id);
  const j = i + by;
  if (i < 0 || j < 0 || j >= list.length) return list;
  const out = [...list];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

/** A price the AI suggested: where it came from, and Accept. Changing the number accepts it too. */
function AiTag({ source, onAccept }: { source: string | null | undefined; onAccept: () => void }) {
  if (!source) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] bg-amber-50 px-2 py-1 text-xs text-amber-900">
      <span className="font-semibold">AI estimate</span>
      <span className="min-w-0 flex-1">{source}</span>
      <button className="font-semibold text-brand hover:underline" onClick={onAccept}>
        Accept
      </button>
    </div>
  );
}

function RowTools({ onUp, onDown, onRemove, label }: { onUp?: () => void; onDown?: () => void; onRemove: () => void; label: string }) {
  return (
    <div className="flex items-center gap-1 text-sm text-muted">
      {onUp && (
        <button className="rounded px-1.5 hover:bg-mist hover:text-ink" aria-label={`Move ${label} up`} onClick={onUp}>
          ↑
        </button>
      )}
      {onDown && (
        <button className="rounded px-1.5 hover:bg-mist hover:text-ink" aria-label={`Move ${label} down`} onClick={onDown}>
          ↓
        </button>
      )}
      <button className="rounded px-1.5 hover:bg-mist hover:text-danger" aria-label={`Remove ${label}`} onClick={onRemove}>
        ×
      </button>
    </div>
  );
}

function AddButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <div className="px-5 py-3">
      <Button variant="secondary" className="px-3 py-1.5" onClick={onClick}>
        {children}
      </Button>
    </div>
  );
}

/** The rep's editor for a custom proposal: sections, allowances, options, terms, live totals and margin. */
export function CustomEditor({ p, onChange }: { p: ProposalDetail; onChange: (d: ProposalDetail) => void }) {
  const [form, setFormState] = useState(p.form as CustomForm);
  // Fields keep what's typed, so a draft replaced by the AI (or an undo) remounts them; and isn't saved back.
  const [formKey, setFormKey] = useState(0);
  const fromServer = useRef(false);
  const setForm = setFormState;
  const replaceForm = (f: CustomForm) => {
    fromServer.current = true;
    setFormState(f);
    setFormKey((k) => k + 1);
  };
  const [aiWorking, setAiWorking] = useState(p.custom!.aiBusy);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ d: ProposalDetail; how: SendHow } | null>(null);
  const min = p.custom!.minMarginPercent;
  const set = (patchF: Partial<CustomForm>) => setForm((f) => ({ ...f, ...patchF }));
  const live = useMemo(() => customTotals(form, min), [form, min]);
  const commercial = form.market === "commercial";

  const save = useMutation({ mutationFn: (f: CustomForm) => patch<ProposalDetail>(`/api/proposals/${p.id}`, { form: f }) });
  const first = useRef(true);
  useEffect(() => {
    if (first.current || fromServer.current) {
      first.current = false;
      fromServer.current = false;
      return;
    }
    const t = window.setTimeout(() => save.mutate(form), 500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const setSection = (id: string, next: Partial<CustomSection>) => set({ sections: swap(form.sections, id, next) });

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_400px]">
      <fieldset key={formKey} disabled={aiWorking} className="flex min-w-0 flex-col gap-5 disabled:opacity-70">
        <Panel>
          <PanelHeader
            title="The proposal"
            action={
              <div className="flex rounded-[var(--radius-control)] border border-line p-0.5 text-sm" role="group" aria-label="Residential or commercial">
                {(["residential", "commercial"] as const).map((m) => (
                  <button
                    key={m}
                    className={cx("rounded-[calc(var(--radius-control)-2px)] px-3 py-1 font-semibold", form.market === m ? "bg-ink text-white" : "text-ink-soft hover:bg-mist")}
                    onClick={() => set({ market: m, marketSetBy: "rep" })}
                  >
                    {CUSTOM_MARKET_LABELS[m]}
                  </button>
                ))}
              </div>
            }
          />
          <div className="flex flex-col gap-4 px-5 py-5">
            <Field label="Title on the proposal" value={form.title} maxLength={120} onChange={(e) => set({ title: e.target.value })} placeholder="Kitchen remodel" />
            <TextAreaField label="Opening paragraph" rows={3} value={form.intro} maxLength={10_000} onChange={(e) => set({ intro: e.target.value })} placeholder="What we'll do, in a few sentences." />
            {commercial && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Project name" value={form.cover.projectName} maxLength={200} onChange={(e) => set({ cover: { ...form.cover, projectName: e.target.value } })} />
                <Field label="Bid date" value={form.cover.bidDate} maxLength={60} onChange={(e) => set({ cover: { ...form.cover, bidDate: e.target.value } })} placeholder="Today" />
                <Field label="Submitted to (company)" value={form.cover.toCompany} maxLength={200} onChange={(e) => set({ cover: { ...form.cover, toCompany: e.target.value } })} />
                <Field label="Attention" value={form.cover.toContact} maxLength={200} onChange={(e) => set({ cover: { ...form.cover, toContact: e.target.value } })} />
                <Field label="Job site (if not the customer's address)" value={form.cover.location} maxLength={300} onChange={(e) => set({ cover: { ...form.cover, location: e.target.value } })} />
                <Field label="Based on (plans, specs, addenda)" value={form.cover.basedOn} maxLength={500} onChange={(e) => set({ cover: { ...form.cover, basedOn: e.target.value } })} />
              </div>
            )}
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="Scope of work" description="One section per part of the job. Price each one as a lump sum or by items, or mark it included in the base price." />
          {form.sections.map((s, i) => (
            <SectionEditor
              key={s.id}
              s={s}
              index={i}
              total={live.sections[i]!.priceCents}
              onChange={(next) => setSection(s.id, next)}
              tools={<RowTools label={s.heading || "section"} onUp={i ? () => set({ sections: move(form.sections, s.id, -1) }) : undefined} onDown={i < form.sections.length - 1 ? () => set({ sections: move(form.sections, s.id, 1) }) : undefined} onRemove={() => set({ sections: form.sections.filter((x) => x.id !== s.id) })} />}
            />
          ))}
          <AddButton onClick={() => set({ sections: [...form.sections, { id: newPartId(), heading: "", description: "", included: [], pricing: "lump", priceCents: 0, costCents: null, items: [], aiSource: null }] })}>Add a section</AddButton>
        </Panel>

        <Panel>
          <PanelHeader title="Allowances" description="Included in the price; over or under is settled by change order. Per unit (tile at $12/sq ft × 180 sq ft) or a set amount." />
          {form.allowances.map((a) => (
            <AllowanceEditor key={a.id} a={a} onChange={(next) => set({ allowances: swap(form.allowances, a.id, next) })} onRemove={() => set({ allowances: form.allowances.filter((x) => x.id !== a.id) })} />
          ))}
          <AddButton onClick={() => set({ allowances: [...form.allowances, { id: newPartId(), description: "", quantity: null, unit: "", unitPriceCents: null, amountCents: 0, costCents: null, aiSource: null }] })}>Add an allowance</AddButton>
        </Panel>

        <Panel>
          <PanelHeader title="Options and alternates" description="The customer ticks the ones they want when signing; the contract total follows. A deduct alternate comes off the price." />
          {form.options.map((o, i) => (
            <OptionEditor key={o.id} o={o} label={optionLabel(o, i, form.options)} onChange={(next) => set({ options: swap(form.options, o.id, next) })} onRemove={() => set({ options: form.options.filter((x) => x.id !== o.id) })} />
          ))}
          <AddButton onClick={() => set({ options: [...form.options, { id: newPartId(), kind: "add", label: "", description: "", amountCents: 0, costCents: null, aiSource: null }] })}>Add an option</AddButton>
        </Panel>

        <Panel>
          <PanelHeader title="Unit prices" description="Prices for extra work if it's needed (not in the total)." />
          {form.unitPrices.map((u) => (
            <UnitPriceEditor key={u.id} u={u} onChange={(next) => set({ unitPrices: swap(form.unitPrices, u.id, next) })} onRemove={() => set({ unitPrices: form.unitPrices.filter((x) => x.id !== u.id) })} />
          ))}
          <AddButton onClick={() => set({ unitPrices: [...form.unitPrices, { id: newPartId(), description: "", unit: "EA", unitPriceCents: 0, aiSource: null }] })}>Add a unit price</AddButton>
        </Panel>

        <Panel>
          <PanelHeader title="Exclusions and clarifications" />
          <div className="grid gap-4 px-5 py-5 md:grid-cols-2">
            <TextAreaField label="Not included (one per line)" rows={5} value={form.exclusions.join("\n")} onChange={(e) => set({ exclusions: linesOf(e.target.value) })} placeholder="Permit fees" />
            <TextAreaField label={commercial ? "Qualifications and clarifications (one per line)" : "Clarifications (one per line)"} rows={5} value={form.qualifications.join("\n")} onChange={(e) => set({ qualifications: linesOf(e.target.value) })} placeholder="Work during normal business hours." />
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="Schedule" />
          <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
            <Field label="Start" value={form.schedule.start} maxLength={200} onChange={(e) => set({ schedule: { ...form.schedule, start: e.target.value } })} placeholder="Within 2 weeks of signing" />
            <Field label="Duration" value={form.schedule.duration} maxLength={200} onChange={(e) => set({ schedule: { ...form.schedule, duration: e.target.value } })} placeholder="About 6 weeks" />
            <TextAreaField className="sm:col-span-2" label="Schedule notes" rows={2} value={form.schedule.notes} maxLength={2000} onChange={(e) => set({ schedule: { ...form.schedule, notes: e.target.value } })} />
          </div>
        </Panel>

        <PaymentEditor form={form} set={set} payments={live.payments} />

        <Panel>
          <PanelHeader title="Price and terms" />
          <div className="grid gap-4 px-5 py-5 sm:grid-cols-3">
            <NumberBox label="Discount" value={form.discountCents || null} format={dollars} parse={parseDollars} onValue={(n) => set({ discountCents: n ?? 0 })} placeholder="0.00" />
            <NumberBox label="Sales tax % (taxable jobs only)" value={form.taxPercentMilli || null} format={(n) => String(n / 1000)} parse={percentBox} onValue={(n) => set({ taxPercentMilli: n ?? 0 })} placeholder="0" />
            <NumberBox label="Valid for (days)" value={form.validDays} format={String} parse={(t) => (/^\d{1,3}$/.test(t.trim()) && Number(t) >= 1 && Number(t) <= 120 ? Number(t) : Number.NaN)} onValue={(n) => set({ validDays: n ?? 15 })} />
            <TextAreaField className="sm:col-span-3" label="Warranty" rows={3} value={form.warranty} maxLength={4000} onChange={(e) => set({ warranty: e.target.value })} />
            <TextAreaField className="sm:col-span-3" label="Notes on the proposal" rows={2} value={form.notes} maxLength={4000} onChange={(e) => set({ notes: e.target.value })} />
            <TextAreaField className="sm:col-span-3" label="The customer fills in when signing (one per line)" rows={2} value={form.selections.join("\n")} onChange={(e) => set({ selections: linesOf(e.target.value).slice(0, 10) })} placeholder="Cabinet color" />
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="What the customer sees" description="They always see the total, the options, and the payments." />
          <div className="flex flex-wrap gap-x-6 gap-y-1 px-5 py-4">
            <Checkbox label="Section prices" checked={form.show.sectionPrices} onChange={(v) => set({ show: { ...form.show, sectionPrices: v } })} />
            <Checkbox label="Item quantities and prices" checked={form.show.itemDetail} onChange={(v) => set({ show: { ...form.show, itemDetail: v } })} />
            <Checkbox label="Allowance amounts" checked={form.show.allowanceAmounts} onChange={(v) => set({ show: { ...form.show, allowanceAmounts: v } })} />
            {form.billing.mode === "progress" && <Checkbox label="Schedule of values" checked={form.show.scheduleOfValues} onChange={(v) => set({ show: { ...form.show, scheduleOfValues: v } })} />}
          </div>
        </Panel>
      </fieldset>

      <aside className="order-first flex flex-col gap-4 xl:sticky xl:top-4 xl:order-none xl:max-h-[calc(100dvh-2rem)] xl:self-start xl:overflow-y-auto">
        {p.aiAvailable && (
          <BuilderChat
            p={p}
            beforeSend={async () => {
              if (!aiWorking) await save.mutateAsync(form);
            }}
            working={aiWorking}
            setWorking={setAiWorking}
            onForm={replaceForm}
            onDone={(d) => {
              replaceForm(d.form as CustomForm);
              onChange(d);
            }}
          />
        )}
        <Panel className="px-5 py-4 text-sm">
          {form.sections.map((s, i) => (
            <div key={s.id} className="flex justify-between gap-3 py-0.5 text-ink-soft">
              <span className="min-w-0 truncate">{s.heading || `Section ${i + 1}`}</span>
              <span>{s.pricing === "included" ? "Included" : formatCents(live.sections[i]!.priceCents)}</span>
            </div>
          ))}
          {live.allowancesCents > 0 && (
            <div className="flex justify-between py-0.5 text-ink-soft">
              <span>Allowances</span>
              <span>{formatCents(live.allowancesCents)}</span>
            </div>
          )}
          {live.discountCents > 0 && (
            <div className="flex justify-between py-0.5 text-ink-soft">
              <span>Discount</span>
              <span>-{formatCents(live.discountCents)}</span>
            </div>
          )}
          {live.taxCents > 0 && (
            <div className="flex justify-between py-0.5 text-ink-soft">
              <span>Sales tax ({formatPercentMilli(form.taxPercentMilli)})</span>
              <span>{formatCents(live.taxCents)}</span>
            </div>
          )}
          <div className="mt-2 flex justify-between border-t border-mist pt-2 text-base font-semibold">
            <span>{form.options.length ? "Base price" : "Total"}</span>
            <span>{formatCents(live.contractCents)}</span>
          </div>
          {form.options.map((o, i) => (
            <div key={o.id} className="flex justify-between gap-3 py-0.5 text-ink-soft">
              <span className="min-w-0 truncate">{optionLabel(o, i, form.options)}</span>
              <span>
                {o.kind === "deduct" ? "-" : "+"}
                {formatCents(o.amountCents)}
              </span>
            </div>
          ))}
          <div className="mt-3 rounded-[var(--radius-control)] bg-mist px-3 py-2">
            <div className="flex justify-between">
              <span>Our cost</span>
              <span>{formatCents(live.costCents)}</span>
            </div>
            <div className={cx("flex justify-between font-semibold", live.belowMinimum && "text-danger")}>
              <span>Margin</span>
              <span>{live.marginPercent === null ? "Enter costs" : `${formatCents(live.marginCents)} (${live.marginPercent}%)`}</span>
            </div>
            <div className="text-xs text-muted">
              Only you see this. Minimum {min}%.{live.missingCosts > 0 && ` ${live.missingCosts} priced part${live.missingCosts === 1 ? " has" : "s have"} no cost yet.`} Allowances without a cost count at their amount.
            </div>
          </div>
          {live.belowMinimum && (
            <div className="mt-3">
              <Alert>Margin is under {min}%. Check the price before sending.</Alert>
            </div>
          )}
        </Panel>
        <Panel className="flex flex-col gap-2 px-5 py-4">
          {live.problems.map((m) => (
            <div key={m} className="text-sm text-muted">
              {m}
            </div>
          ))}
          {save.error && <Alert>{errorMessage(save.error)}</Alert>}
          <a href={`/api/proposals/${p.id}/preview.pdf`} target="_blank" rel="noreferrer" onClick={() => save.mutate(form)} className="inline-flex justify-center rounded-[var(--radius-control)] bg-mist px-4 py-2 text-sm font-semibold hover:bg-line">
            Preview PDF
          </a>
          <Button variant="primary" disabled={live.problems.length > 0 || aiWorking} onClick={async () => (await save.mutateAsync(form), setSending(true))}>
            {p.custom!.revision ? `Send Rev ${p.custom!.revision} to customer…` : "Send to customer…"}
          </Button>
          {p.custom!.revisionOf && <p className="text-xs text-muted">Sending this revision replaces the one it revises: that link stops working.</p>}
        </Panel>
      </aside>
      {sending && <RetailSendDialog p={p} form={form} total={live.contractCents} margin={live.belowMinimum ? live.marginPercent : null} onClose={() => setSending(false)} onSent={(d, how) => (setSending(false), setSent({ d, how }))} />}
      {sent && <LinkDialog p={sent.d} how={sent.how} onClose={() => onChange(sent.d)} />}
    </div>
  );
}

/** The AI that builds the proposal with the rep: a conversation beside the draft. Its edits show up in the draft as it makes them. */
function BuilderChat({ p, beforeSend, working, setWorking, onForm, onDone }: { p: ProposalDetail; beforeSend: () => Promise<void>; working: boolean; setWorking: (b: boolean) => void; onForm: (f: CustomForm) => void; onDone: (d: ProposalDetail) => void }) {
  const [entries, setEntries] = useState<CustomChatEntry[]>((p.chat as unknown as CustomChatEntry[]) ?? []);
  const [text, setText] = useState("");
  const [live, setLive] = useState<{ text: string; steps: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => end.current?.scrollIntoView({ block: "nearest" }), [entries.length, live?.text, live?.steps.length]);

  // Opened while the AI was still working (another tab, or a reload): wait for it.
  useEffect(() => {
    if (!working || live) return;
    const t = window.setInterval(async () => {
      const d = await get<ProposalDetail>(`/api/proposals/${p.id}`).catch(() => null);
      if (d && !d.custom?.aiBusy) {
        setWorking(false);
        setEntries((d.chat as unknown as CustomChatEntry[]) ?? []);
        onDone(d);
      }
    }, 3000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [working, live]);

  const send = async (message: string) => {
    if (!message.trim() || working) return;
    setError(null);
    setText("");
    try {
      await beforeSend();
    } catch (e) {
      setError(errorMessage(e));
      return;
    }
    setWorking(true);
    setEntries((xs) => [...xs, { role: "user", text: message, at: new Date().toISOString() }]);
    setLive({ text: "", steps: [] });
    try {
      await streamPost<CustomChatEvent>(`/api/proposals/${p.id}/build-chat`, { message }, (e) => {
        if (e.type === "text") setLive((l) => (l ? { ...l, text: l.text + e.text } : l));
        else if (e.type === "step") {
          setLive((l) => (l ? { ...l, steps: [...l.steps, e.text] } : l));
          if (e.form) onForm(e.form);
        } else if (e.type === "error") setError(e.message);
      });
    } catch (e) {
      setError(errorMessage(e));
    }
    const d = await get<ProposalDetail>(`/api/proposals/${p.id}`).catch(() => null);
    setLive(null);
    setWorking(false);
    // Not saved (the connection dropped): put the message back in the box to send again.
    const chat = (d?.chat as unknown as CustomChatEntry[] | undefined) ?? [];
    if (!chat.some((x) => x.role === "user" && x.text === message)) setText((t) => t || message);
    if (d) {
      setEntries(chat);
      onDone(d);
    }
  };
  const undo = useMutation({
    mutationFn: (versionId: string) => post<ProposalDetail>(`/api/proposals/${p.id}/versions/${versionId}/restore`),
    onSuccess: (d) => {
      setEntries((d.chat as unknown as CustomChatEntry[]) ?? []);
      onDone(d);
    },
  });
  const starters = [
    "Here's the job: ",
    "Read the plans and sub bids in the job's files and draft the scope.",
    "Suggest prices for anything without one, and tell me where they come from.",
    "What am I missing? Exclusions, permits, risks.",
  ];
  return (
    <Panel className="flex flex-col">
      <PanelHeader title="Build it with AI" description="Tell it about the job; it asks what's missing and drafts the proposal on the left. Prices it suggests are marked until you accept them." />
      <div className="flex max-h-[55dvh] min-h-40 flex-col gap-3 overflow-y-auto px-5 py-4 text-sm">
        {!entries.length && !live && (
          <div className="flex flex-col gap-2">
            <p className="text-muted">Start with what you know: the work, rough sizes, who supplies what, a price if you have one. Or try:</p>
            {starters.map((s) => (
              <button key={s} className="rounded-[var(--radius-control)] border border-line px-3 py-2 text-left hover:border-ink-soft" onClick={() => setText(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
        {entries.map((e, i) => (
          <div key={i} className={cx("flex flex-col gap-1", e.role === "user" && "items-end")}>
            <div className={cx("max-w-[92%] rounded-2xl px-3 py-2", e.role === "user" ? "bg-ink text-white" : "bg-mist")}>
              {e.role === "user" ? <p className="whitespace-pre-wrap">{e.text}</p> : <FredText text={e.text} />}
            </div>
            {e.steps?.map((s, k) => (
              <div key={k} className="text-xs text-muted">
                · {s}
              </div>
            ))}
            {e.versionId && (
              <button className="self-start text-xs font-semibold text-brand hover:underline disabled:opacity-50" disabled={working || undo.isPending} onClick={() => window.confirm("Put the draft back the way it was before this?") && undo.mutate(e.versionId!)}>
                Undo this change
              </button>
            )}
          </div>
        ))}
        {live && (
          <div className="flex flex-col gap-1">
            {live.text && (
              <div className="max-w-[92%] rounded-2xl bg-mist px-3 py-2">
                <FredText text={live.text} />
              </div>
            )}
            {live.steps.map((s, k) => (
              <div key={k} className="text-xs text-muted">
                · {s}
              </div>
            ))}
            <div className="flex items-center gap-2 text-xs text-muted">
              <span className="size-3 animate-spin rounded-full border-2 border-brand border-r-transparent" aria-hidden />
              Working…
            </div>
          </div>
        )}
        {working && !live && <div className="text-xs text-muted">The AI is still working on this proposal…</div>}
        {(error || undo.error) && <Alert>{error ?? errorMessage(undo.error)}</Alert>}
        <div ref={end} />
      </div>
      <form
        className="flex items-end gap-2 border-t border-mist px-5 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send(text);
        }}
      >
        <textarea
          aria-label="Message the AI"
          rows={2}
          value={text}
          disabled={working}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send(text);
            }
          }}
          placeholder="Kitchen remodel, tear out to studs, new cabinets and quartz, LVP floor, about $48k, 6 weeks…"
          className="min-h-11 flex-1 resize-y rounded-[var(--radius-control)] border border-line px-3 py-2 text-base focus:border-brand focus:outline-none sm:text-[15px]"
        />
        <Button type="submit" variant="primary" busy={working} disabled={!text.trim()}>
          Send
        </Button>
      </form>
    </Panel>
  );
}

function SectionEditor({ s, index, total, onChange, tools }: { s: CustomSection; index: number; total: number; onChange: (next: Partial<CustomSection>) => void; tools: ReactNode }) {
  const setItem = (id: string, next: Partial<CustomItem>) => onChange({ items: swap(s.items, id, next) });
  return (
    <div className="flex flex-col gap-3 border-b border-mist px-5 py-4">
      <div className="flex items-start gap-2">
        <span className="mt-8 text-sm font-semibold text-muted">{index + 1}.</span>
        <div className="grid flex-1 gap-3 sm:grid-cols-[1fr_220px]">
          <Field label="Heading" value={s.heading} maxLength={200} onChange={(e) => onChange({ heading: e.target.value })} placeholder="Cabinets" />
          <SelectField label="Priced" value={s.pricing} onChange={(e) => onChange({ pricing: e.target.value as CustomSection["pricing"] })}>
            <option value="lump">As a lump sum</option>
            <option value="items">By items</option>
            <option value="included">Included in the base price</option>
          </SelectField>
        </div>
        <div className="mt-7">{tools}</div>
      </div>
      <TextAreaField label="What we'll do" rows={3} value={s.description} maxLength={10_000} onChange={(e) => onChange({ description: e.target.value })} />
      <TextAreaField label="What's included (one per line)" rows={3} value={s.included.join("\n")} onChange={(e) => onChange({ included: linesOf(e.target.value) })} />
      {s.pricing === "lump" && (
        <div className="grid gap-3 sm:grid-cols-2">
          <NumberBox key={`p${s.aiSource ? "ai" : ""}`} label="Price" value={s.priceCents || null} format={dollars} parse={parseDollars} onValue={(n) => onChange({ priceCents: n ?? 0, aiSource: null })} placeholder="0.00" />
          <NumberBox label="Our cost (only you see it)" value={s.costCents} format={dollars} parse={parseDollars} onValue={(n) => onChange({ costCents: n })} />
        </div>
      )}
      {s.pricing === "included" && <NumberBox label="Our cost (only you see it)" className="sm:w-1/2" value={s.costCents} format={dollars} parse={parseDollars} onValue={(n) => onChange({ costCents: n })} />}
      {s.pricing !== "items" && <AiTag source={s.aiSource} onAccept={() => onChange({ aiSource: null })} />}
      {s.pricing === "items" && (
        <div className="flex flex-col gap-2">
          {s.items.map((it) => (
            <div key={it.id} className="flex flex-col gap-1 rounded-[var(--radius-control)] bg-mist/50 p-2">
              <div className="grid grid-cols-2 items-end gap-2 sm:grid-cols-[100px_80px_120px_120px_minmax(0,1fr)]">
                <Field className="col-span-full" label="Item" value={it.description} maxLength={500} onChange={(e) => setItem(it.id, { description: e.target.value })} />
                <NumberBox label="Qty" value={it.quantity || null} format={formatQuantity} parse={qtyBox} onValue={(n) => setItem(it.id, { quantity: n ?? 0 })} />
                <Field label="Unit" value={it.unit} maxLength={20} onChange={(e) => setItem(it.id, { unit: e.target.value })} />
                <NumberBox key={`u${it.aiSource ? "ai" : ""}`} label="Unit price" value={it.unitPriceCents || null} format={dollars} parse={parseDollars} onValue={(n) => setItem(it.id, { unitPriceCents: n ?? 0, aiSource: null })} />
                <NumberBox label="Unit cost" value={it.unitCostCents} format={dollars} parse={parseDollars} onValue={(n) => setItem(it.id, { unitCostCents: n })} />
                <div className="flex items-center justify-end gap-2 pb-2 text-sm">
                  <span className="text-ink-soft">{formatCents(extend(it.quantity, it.unitPriceCents))}</span>
                  <RowTools label="item" onRemove={() => onChange({ items: s.items.filter((x) => x.id !== it.id) })} />
                </div>
              </div>
              <AiTag source={it.aiSource} onAccept={() => setItem(it.id, { aiSource: null })} />
            </div>
          ))}
          <div className="flex items-center justify-between">
            <Button variant="quiet" className="px-3 py-1.5" onClick={() => onChange({ items: [...s.items, { id: newPartId(), description: "", quantity: 1000, unit: "EA", unitPriceCents: 0, unitCostCents: null, aiSource: null }] })}>
              Add an item
            </Button>
            <span className="text-sm font-semibold">Section total {formatCents(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function AllowanceEditor({ a, onChange, onRemove }: { a: CustomAllowance; onChange: (next: Partial<CustomAllowance>) => void; onRemove: () => void }) {
  const perUnit = a.quantity !== null && a.unitPriceCents !== null;
  return (
    <div className="flex flex-col gap-2 border-b border-mist px-5 py-4">
      <div className="flex items-end gap-2">
        <Field className="flex-1" label="Allowance" value={a.description} maxLength={300} onChange={(e) => onChange({ description: e.target.value })} placeholder="Backsplash tile" />
        <SelectField className="w-36" label="Priced" value={perUnit ? "unit" : "lump"} onChange={(e) => onChange(e.target.value === "unit" ? { quantity: a.quantity ?? 1000, unitPriceCents: a.unitPriceCents ?? 0 } : { quantity: null, unitPriceCents: null })}>
          <option value="lump">Set amount</option>
          <option value="unit">Per unit</option>
        </SelectField>
        <div className="pb-2">
          <RowTools label="allowance" onRemove={onRemove} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {perUnit ? (
          <>
            <NumberBox label="Quantity" value={a.quantity || null} format={formatQuantity} parse={qtyBox} onValue={(n) => onChange({ quantity: n ?? 0 })} />
            <Field label="Unit" value={a.unit} maxLength={20} onChange={(e) => onChange({ unit: e.target.value })} placeholder="sq ft" />
            <NumberBox key={`u${a.aiSource ? "ai" : ""}`} label="Per unit" value={a.unitPriceCents || null} format={dollars} parse={parseDollars} onValue={(n) => onChange({ unitPriceCents: n ?? 0, aiSource: null })} />
          </>
        ) : (
          <NumberBox key={`a${a.aiSource ? "ai" : ""}`} label="Amount" value={a.amountCents || null} format={dollars} parse={parseDollars} onValue={(n) => onChange({ amountCents: n ?? 0, aiSource: null })} />
        )}
        <NumberBox label="Our cost" value={a.costCents} format={dollars} parse={parseDollars} onValue={(n) => onChange({ costCents: n })} />
      </div>
      {perUnit && <div className="text-sm text-ink-soft">Allowance {formatCents(extend(a.quantity!, a.unitPriceCents!))}</div>}
      <AiTag source={a.aiSource} onAccept={() => onChange({ aiSource: null })} />
    </div>
  );
}

function OptionEditor({ o, label, onChange, onRemove }: { o: CustomOption; label: string; onChange: (next: Partial<CustomOption>) => void; onRemove: () => void }) {
  return (
    <div className="flex flex-col gap-2 border-b border-mist px-5 py-4">
      <div className="grid grid-cols-2 items-end gap-2 sm:grid-cols-[140px_140px_minmax(0,1fr)_auto]">
        <SelectField label="Kind" value={o.kind} onChange={(e) => onChange({ kind: e.target.value as CustomOption["kind"] })}>
          <option value="add">Add-on</option>
          <option value="deduct">Deduct alternate</option>
        </SelectField>
        <Field label="Label" value={o.label} maxLength={60} onChange={(e) => onChange({ label: e.target.value })} placeholder={label} />
        <Field className="col-span-2 sm:col-span-1" label="Description" value={o.description} maxLength={1000} onChange={(e) => onChange({ description: e.target.value })} placeholder="Under-cabinet lighting" />
        <div className="pb-2">
          <RowTools label="option" onRemove={onRemove} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:w-1/2">
        <NumberBox key={`a${o.aiSource ? "ai" : ""}`} label={o.kind === "deduct" ? "Comes off the price" : "Adds to the price"} value={o.amountCents || null} format={dollars} parse={parseDollars} onValue={(n) => onChange({ amountCents: n ?? 0, aiSource: null })} />
        <NumberBox label={o.kind === "deduct" ? "Cost we save" : "Our cost"} value={o.costCents} format={dollars} parse={parseDollars} onValue={(n) => onChange({ costCents: n })} />
      </div>
      <AiTag source={o.aiSource} onAccept={() => onChange({ aiSource: null })} />
    </div>
  );
}

function UnitPriceEditor({ u, onChange, onRemove }: { u: CustomUnitPrice; onChange: (next: Partial<CustomUnitPrice>) => void; onRemove: () => void }) {
  return (
    <div className="flex flex-col gap-2 border-b border-mist px-5 py-4">
      <div className="grid grid-cols-2 items-end gap-2 sm:grid-cols-[minmax(0,1fr)_90px_130px_auto]">
        <Field className="col-span-2 sm:col-span-1" label="Extra work" value={u.description} maxLength={300} onChange={(e) => onChange({ description: e.target.value })} placeholder="Additional decking" />
        <Field label="Per" value={u.unit} maxLength={20} onChange={(e) => onChange({ unit: e.target.value })} placeholder="sheet" />
        <NumberBox key={`p${u.aiSource ? "ai" : ""}`} label="Price" value={u.unitPriceCents || null} format={dollars} parse={parseDollars} onValue={(n) => onChange({ unitPriceCents: n ?? 0, aiSource: null })} />
        <div className="pb-2">
          <RowTools label="unit price" onRemove={onRemove} />
        </div>
      </div>
      <AiTag source={u.aiSource} onAccept={() => onChange({ aiSource: null })} />
    </div>
  );
}

/** "30%" or "$1,500" → a percent or an amount. */
function parsePayment(t: string): Pick<CustomMilestone, "percentMilli" | "amountCents"> | null {
  const s = t.trim();
  if (!s) return { percentMilli: null, amountCents: null };
  if (s.endsWith("%")) {
    const p = parsePercentMilli(s);
    return p === null ? null : { percentMilli: p, amountCents: null };
  }
  const d = parseDollars(s);
  return d === null || Number.isNaN(d) ? null : { percentMilli: null, amountCents: d };
}
const showPayment = (m: CustomMilestone) => (m.percentMilli !== null ? formatPercentMilli(m.percentMilli) : m.amountCents !== null ? `$${dollars(m.amountCents)}` : "");

function PaymentBox({ m, onChange }: { m: CustomMilestone; onChange: (next: Pick<CustomMilestone, "percentMilli" | "amountCents">) => void }) {
  const [text, setText] = useState(showPayment(m));
  const bad = parsePayment(text) === null;
  return (
    <Field
      label="Percent or amount"
      value={text}
      placeholder="30% or $500"
      error={bad ? "Like 30% or $500" : undefined}
      onChange={(e) => {
        setText(e.target.value);
        const v = parsePayment(e.target.value);
        if (v) onChange(v);
      }}
    />
  );
}

function PaymentEditor({ form, set, payments }: { form: CustomForm; set: (f: Partial<CustomForm>) => void; payments: ReturnType<typeof customTotals>["payments"] }) {
  const b = form.billing;
  const setBilling = (next: Partial<CustomForm["billing"]>) => set({ billing: { ...b, ...next } });
  const ms = b.milestones;
  const sov = b.mode === "progress" ? formSov(form) : [];
  return (
    <Panel>
      <PanelHeader title="Payment" description={b.mode === "milestones" ? "Payments as the work goes. The last one is the rest, so they always add up." : "Monthly progress bills against the schedule of values, with retainage."} />
      <div className="flex flex-col gap-4 px-5 py-5">
        <SelectField className="sm:w-1/2" label="Billing" value={b.mode} onChange={(e) => setBilling({ mode: e.target.value as CustomForm["billing"]["mode"], ...(e.target.value === "milestones" && !ms.length ? { milestones: [{ id: newPartId(), label: "Due on completion", when: "When the work is complete", percentMilli: null, amountCents: null }] } : {}) })}>
          <option value="milestones">Payments at milestones</option>
          <option value="progress">Progress billing (schedule of values, retainage)</option>
        </SelectField>
        {b.mode === "milestones" ? (
          <div className="flex flex-col gap-2">
            {ms.map((m, i) => {
              const last = i === ms.length - 1;
              const pay = payments[i];
              return (
                <div key={m.id} className="grid grid-cols-2 items-end gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_150px_110px_auto]">
                  <Field label="Payment" value={m.label} maxLength={120} onChange={(e) => setBilling({ milestones: swap(ms, m.id, { label: e.target.value }) })} placeholder={i === 0 ? "Deposit" : "Cabinets set"} />
                  <Field label="When" value={m.when} maxLength={300} onChange={(e) => setBilling({ milestones: swap(ms, m.id, { when: e.target.value }) })} placeholder={i === 0 ? "When this agreement is signed" : "When the cabinets are installed"} />
                  {last ? <div className="pb-2 text-sm text-muted">The rest</div> : <PaymentBox m={m} onChange={(v) => setBilling({ milestones: swap(ms, m.id, v) })} />}
                  <div className={cx("pb-2 text-right text-sm font-semibold", pay && pay.amountCents < 0 && "text-danger")}>{pay ? formatCents(pay.amountCents) : ""}</div>
                  <div className="pb-2">
                    <RowTools label="payment" onUp={i ? () => setBilling({ milestones: move(ms, m.id, -1) }) : undefined} onRemove={() => setBilling({ milestones: ms.filter((x) => x.id !== m.id) })} />
                  </div>
                </div>
              );
            })}
            <div>
              <Button
                variant="quiet"
                className="px-3 py-1.5"
                onClick={() => {
                  // New payments go before the last one (the rest).
                  const add: CustomMilestone = { id: newPartId(), label: ms.length > 1 ? "" : "Deposit", when: ms.length > 1 ? "" : "When this agreement is signed", percentMilli: 30_000, amountCents: null };
                  setBilling({ milestones: ms.length ? [...ms.slice(0, -1), add, ms[ms.length - 1]!] : [add, { id: newPartId(), label: "Balance", when: "When the work is complete", percentMilli: null, amountCents: null }] });
                }}
              >
                Add a payment
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <NumberBox label="Retainage %" value={b.retainagePercentMilli || null} format={(n) => String(n / 1000)} parse={percentBox} onValue={(n) => setBilling({ retainagePercentMilli: Math.min(n ?? 0, 50_000) })} placeholder="5" />
              <Field label="Payment terms" value={b.terms} maxLength={200} onChange={(e) => setBilling({ terms: e.target.value })} placeholder="Net 30" />
            </div>
            <Checkbox label="Retainage drops partway through" checked={!!b.stepDown} onChange={(v) => setBilling({ stepDown: v ? { atCompletePercent: 50, toPercentMilli: Math.round(b.retainagePercentMilli / 2) } : null })} />
            {b.stepDown && (
              <div className="grid gap-3 sm:grid-cols-3">
                <NumberBox label="Once the work is this % complete" value={b.stepDown.atCompletePercent} format={String} parse={(t) => (/^\d{1,2}$/.test(t.trim()) && Number(t) >= 1 ? Number(t) : Number.NaN)} onValue={(n) => setBilling({ stepDown: { ...b.stepDown!, atCompletePercent: n ?? 50 } })} />
                <NumberBox label="Retainage drops to %" value={b.stepDown.toPercentMilli} format={(n) => String(n / 1000)} parse={percentBox} onValue={(n) => setBilling({ stepDown: { ...b.stepDown!, toPercentMilli: Math.min(n ?? 0, 50_000) } })} />
              </div>
            )}
            <Checkbox label="Bills may include materials stored on site" checked={b.storedMaterials} onChange={(v) => setBilling({ storedMaterials: v })} />
            <div className="rounded-[var(--radius-control)] border border-line text-sm">
              <div className="border-b border-mist px-3 py-2 font-semibold">Schedule of values (base price)</div>
              {sov.map((l) => (
                <div key={l.key} className="flex justify-between gap-3 px-3 py-1">
                  <span className="min-w-0 truncate">{l.description}</span>
                  <span>{formatCents(l.valueCents)}</span>
                </div>
              ))}
              <p className="px-3 py-2 text-xs text-muted">Each priced section and allowance is a line. Options the customer picks are added when they sign.</p>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

